import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { companyData } from '../data';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();
  const { lang, toggleLanguage, navigation } = useLanguage();

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-[100px]">
          <div className="flex items-center">
            <Link to={lang === 'da' ? '/' : '/en'} className="flex-shrink-0 flex items-center gap-4">
              <img
                className="h-[72px] w-auto transition-all duration-300 hover:scale-105 hover:opacity-90"
                src={getOptimizedImageUrl(companyData.logo, 200, 90)}
                alt={`${companyData.name} Logo`}
              />
              <span className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950 whitespace-nowrap">
                {companyData.name}
              </span>
            </Link>
          </div>
          
          <div className="hidden lg:flex items-center space-x-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-semibold uppercase tracking-wider transition-colors hover:text-blue-700 whitespace-nowrap ${
                  location.pathname === item.href ? 'text-blue-700' : 'text-slate-600'
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Language Selector (Desktop) */}
            <div className="flex items-center gap-1 border-l border-slate-200 pl-6 ml-4">
              <button
                onClick={() => toggleLanguage('da')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                  lang === 'da'
                    ? 'bg-blue-700 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
                aria-label="Skift til dansk"
              >
                <span>DK</span>
              </button>
              <button
                onClick={() => toggleLanguage('en')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                  lang === 'en'
                    ? 'bg-blue-700 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
                aria-label="Switch to English"
              >
                <span>EN</span>
              </button>
            </div>
          </div>

          <div className="flex items-center lg:hidden gap-4">
            {/* Language Selector (Mobile Quick Toggle) */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => toggleLanguage('da')}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase tracking-wider transition-all ${
                  lang === 'da'
                    ? 'bg-blue-700 text-white'
                    : 'text-slate-500 bg-slate-100'
                }`}
              >
                <span>DK</span>
              </button>
              <button
                onClick={() => toggleLanguage('en')}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase tracking-wider transition-all ${
                  lang === 'en'
                    ? 'bg-blue-700 text-white'
                    : 'text-slate-500 bg-slate-100'
                }`}
              >
                <span>EN</span>
              </button>
            </div>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-slate-600 hover:text-slate-900 focus:outline-none"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-slate-50 border-b border-slate-200"
          >
            <div className="px-4 pt-2 pb-6 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-4 text-base font-medium uppercase tracking-wide transition-all ${
                    location.pathname === item.href
                      ? 'text-blue-700 bg-blue-50 border-l-4 border-blue-700 pl-2'
                      : 'text-slate-700 hover:text-blue-700 hover:bg-slate-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
